/**
 * Calendar-date helpers treat a date as a year/month/day with no timezone shift.
 * Use for birth dates, move-in dates, exam dates, etc.
 */

const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** Local noon avoids DST edge cases at midnight. */
function calendarDateFromParts(year: number, monthIndex: number, day: number): Date {
    return new Date(year, monthIndex, day, 12, 0, 0, 0);
}

/**
 * Parse API or form values into a Date representing a calendar day in the user's locale.
 */
export function parseCalendarDate(val: unknown): Date | null {
    if (val == null || val === '') return null;

    if (val instanceof Date && !isNaN(val.getTime())) {
        return calendarDateFromParts(val.getFullYear(), val.getMonth(), val.getDate());
    }

    if (typeof val === 'string') {
        const match = val.match(CALENDAR_DATE_RE);
        if (match) {
            const y = parseInt(match[1], 10);
            const m = parseInt(match[2], 10) - 1;
            const d = parseInt(match[3], 10);
            const dt = calendarDateFromParts(y, m, d);
            return isNaN(dt.getTime()) ? null : dt;
        }
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
            return calendarDateFromParts(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
        }
    }

    return null;
}

/** Format a calendar date for API JSON (date-only, no timezone). */
export function formatCalendarDateForApi(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Replace Date values on the given keys with YYYY-MM-DD strings before sending to the API. */
export function serializeCalendarDateFields<T>(data: T, keys: string[]): T {
    if (data == null || typeof data !== 'object') return data;

    const out = { ...(data as object) } as T;
    for (const key of keys) {
        const v = (out as Record<string, unknown>)[key];
        if (v instanceof Date && !isNaN(v.getTime())) {
            (out as Record<string, unknown>)[key] = formatCalendarDateForApi(v);
        }
    }
    return out;
}
